/**
 * RevenueTrendChart Component
 * Line chart showing revenue vs costs over time
 */

import { useSnapshot } from "valtio";
import { financialStore } from "../stores/financial";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Area,
  ComposedChart,
} from "recharts";

export default function RevenueTrendChart() {
  const { revenueTrend, loading } = useSnapshot(financialStore);

  if (loading && !revenueTrend) {
    return <Skeleton className="h-[350px]" />;
  }

  if (!revenueTrend || revenueTrend.monthly.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Revenue Trend</CardTitle>
        </CardHeader>
        <CardContent className="h-[300px] flex items-center justify-center">
          <p className="text-muted-foreground">No revenue data available</p>
        </CardContent>
      </Card>
    );
  }

  // Transform data for chart
  const data = revenueTrend.monthly.map((m) => ({
    month: formatMonth(m.month),
    revenue: m.revenue / 100, // Convert cents to dollars
    costs: m.costs / 100,
    netProfit: m.netProfit / 100,
    subscribers: m.subscribers,
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Revenue Trend</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart
            data={data}
            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis dataKey="month" className="text-xs fill-muted-foreground" />
            <YAxis
              className="text-xs fill-muted-foreground"
              tickFormatter={(value) => `$${value}`}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: "hsl(var(--card))",
                border: "1px solid hsl(var(--border))",
                borderRadius: "8px",
              }}
              formatter={(value: number, name: string) => {
                if (name === "subscribers") {
                  return [value, "Subscribers"];
                }
                return [`$${value.toFixed(2)}`, formatLegendName(name)];
              }}
            />
            <Legend formatter={formatLegendName} />
            <Area
              type="monotone"
              dataKey="revenue"
              fill="#10b98133"
              stroke="#10b981"
              strokeWidth={2}
              name="revenue"
            />
            <Line
              type="monotone"
              dataKey="costs"
              stroke="#ef4444"
              strokeWidth={2}
              dot={{ fill: "#ef4444", strokeWidth: 2 }}
              name="costs"
            />
            <Line
              type="monotone"
              dataKey="netProfit"
              stroke="#667eea"
              strokeWidth={2}
              strokeDasharray="5 5"
              dot={{ fill: "#667eea", strokeWidth: 2 }}
              name="netProfit"
            />
          </ComposedChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

function formatMonth(month: string): string {
  // Convert "2025-01" to "Jan 25"
  const [year, monthNum] = month.split("-");
  const date = new Date(parseInt(year), parseInt(monthNum) - 1);
  return date.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
}

function formatLegendName(name: string): string {
  const names: Record<string, string> = {
    revenue: "Revenue",
    costs: "Costs",
    netProfit: "Net Profit",
    subscribers: "Subscribers",
  };
  return names[name] ?? name;
}
